import { useEffect, useRef, useState } from "react";

import { useAuth } from "/imports/state/AuthContext";
import api from "/imports/api/client";

/**
 * Loads the redeemable credits for the cart's primary item and derives the
 * order total. v2 today carries a single bookish per cart, so packages,
 * vouchers and membership are fetched/applied against `primaryItem` only.
 *
 * Pricing rules mirror partners' backend:
 * - A package always fully covers the primary item (it wins over everything).
 * - Membership reduces the BASE price (defaultPrice → memberPrice) but only
 *   when no package is picked — paidPlan holds an "access" (membership) OR a
 *   "ppack" (package) entry, never both.
 * - A voucher then eats into whatever residual remains.
 */
// Which pack to spend when several cover the session: the one that dies first.
// Sessions on a pack about to expire are the ones the client stands to lose, and
// a pack with no expiry can always wait.
const bestPackage = (packages) =>
  [...packages].sort((a, b) => {
    if (a.noExpiry !== b.noExpiry) return a.noExpiry ? 1 : -1;
    return (a.expiryDate || "") < (b.expiryDate || "") ? -1 : 1;
  })[0] || null;

export default function useCheckoutPricing({ slug, primaryItem, items }) {
  const { isAuthenticated, activeClientId } = useAuth();

  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  // Set the moment the client touches the credit radios. From then on the
  // auto-selection stands down for this cart item — switching to "Pay" must not
  // be undone by the next refetch.
  const userChoseRef = useRef(false);
  const choosePackage = (id) => {
    userChoseRef.current = true;
    setSelectedPackageId(id);
  };

  // Split seats are card-only — the server refuses a credit on one — so turning
  // it on clears them. Deliberately NOT through choosePackage: this is the split
  // switch talking, not the client picking how to pay, and marking it as their
  // choice would leave the pack unapplied after they switched split back off.
  const setSplitMode = (on) => {
    if (on) {
      setSelectedPackageId("");
      setSelectedVoucherId("");
      return;
    }
    if (!userChoseRef.current) setSelectedPackageId(bestPackage(packages)?._id || "");
  };

  const [vouchers, setVouchers] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState("");

  // Membership auto-applies when active and the service grants member pricing.
  // We surface it as a read-only indicator (no toggle) since partners auto-picks
  // it server-side too.
  const [membership, setMembership] = useState(null);

  // Boolean rather than the object itself: a new cart identity each render
  // would otherwise retrigger the lookups.
  const isSplitSeat = !!primaryItem?.split;

  useEffect(() => {
    userChoseRef.current = false;
    setSelectedPackageId("");
    // A split-booking seat is payable by card only — the server rejects a
    // package or voucher on it. Skip the lookup so nothing is offered that
    // would fail at Complete Booking.
    if (!isAuthenticated || !primaryItem?.serviceId || !primaryItem?.locationId || isSplitSeat) {
      setPackages([]);
      return;
    }
    let cancelled = false;
    setPackagesLoading(true);
    api
      .getRedeemablePackages(slug, {
        serviceId: primaryItem.serviceId,
        locationId: primaryItem.locationId,
        pricingId: primaryItem.pricingId,
        duration: primaryItem.duration,
        // A pack whose validity ends before the session's date can't pay for
        // it — the server drops those from the list.
        date: primaryItem.date
      })
      .then((res) => {
        if (cancelled) return;
        const items = res?.items || [];
        setPackages(items);
        // A client holding a pack that covers this session came to spend it —
        // leaving "Pay" selected asks them to find a radio button before the
        // credit they already own counts, and the ones who miss it pay twice.
        // Only ever pre-selects: "Pay" and every other choice stay one tap away,
        // and a deliberate choice is never overwritten (see userChoseRef).
        if (!userChoseRef.current) setSelectedPackageId(bestPackage(items)?._id || "");
      })
      .catch(() => {
        if (cancelled) return;
        setPackages([]);
      })
      .finally(() => {
        if (cancelled) return;
        setPackagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, slug, primaryItem?.serviceId, primaryItem?.locationId, primaryItem?.pricingId, primaryItem?.duration, primaryItem?.date, isSplitSeat, activeClientId]);

  useEffect(() => {
    setSelectedVoucherId("");
    if (!isAuthenticated || !primaryItem?.locationId || isSplitSeat) {
      setVouchers([]);
      return;
    }
    let cancelled = false;
    setVouchersLoading(true);
    api
      .getRedeemableVouchers(slug, { locationId: primaryItem.locationId })
      .then((res) => {
        if (cancelled) return;
        setVouchers(res?.items || []);
      })
      .catch(() => {
        if (cancelled) return;
        setVouchers([]);
      })
      .finally(() => {
        if (cancelled) return;
        setVouchersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, slug, primaryItem?.locationId, isSplitSeat, activeClientId]);

  useEffect(() => {
    if (!isAuthenticated || !primaryItem?.serviceId || !primaryItem?.locationId) {
      setMembership(null);
      return;
    }
    let cancelled = false;
    api
      .getActiveMembership(slug, {
        serviceId: primaryItem.serviceId,
        locationId: primaryItem.locationId,
        pricingId: primaryItem.pricingId,
        date: primaryItem.date,
        time: primaryItem.time
      })
      .then((res) => {
        if (cancelled) return;
        setMembership(res?.membership || null);
      })
      .catch(() => {
        if (cancelled) return;
        setMembership(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    slug,
    primaryItem?.serviceId,
    primaryItem?.locationId,
    primaryItem?.pricingId,
    primaryItem?.date,
    primaryItem?.time,
    activeClientId
  ]);

  const isPackageApplied = !!selectedPackageId;
  const selectedPackage = packages.find((p) => p._id === selectedPackageId) || null;
  const selectedVoucher = vouchers.find((v) => v._id === selectedVoucherId) || null;

  const primaryPrice = parseFloat(primaryItem?.price) || 0;

  // A redeemed session is worth what the pack paid for it, not the walk-in
  // price it stands in for — the same figure the club's booking log records.
  // Older packs that can't be valued fall back to the walk-in price.
  const packageSessionValue = isPackageApplied && selectedPackage?.sessionValue != null ? selectedPackage.sessionValue : primaryPrice;
  const membershipApplies = !!membership && !isPackageApplied && (membership.saving || 0) > 0;
  const membershipSaving = membershipApplies ? parseFloat(membership.saving) || 0 : 0;

  // Residual after the package (the package covers the primary item's full
  // price when applied — mirrors backend behavior).
  const primaryAfterPackage = isPackageApplied ? 0 : Math.max(0, primaryPrice - membershipSaving);
  const voucherUsed = selectedVoucher ? Math.min(parseFloat(selectedVoucher.remainValue) || 0, primaryAfterPackage) : 0;

  const total = items.reduce((sum, item, idx) => {
    if (idx === 0) return sum + Math.max(0, primaryAfterPackage - voucherUsed);
    return sum + (parseFloat(item.price) || 0);
  }, 0);

  return {
    packages,
    packagesLoading,
    selectedPackageId,
    setSelectedPackageId: choosePackage,
    setSplitMode,
    vouchers,
    vouchersLoading,
    selectedVoucherId,
    setSelectedVoucherId,
    membership,
    isPackageApplied,
    selectedPackage,
    packageSessionValue,
    selectedVoucher,
    membershipApplies,
    membershipSaving,
    primaryAfterPackage,
    voucherUsed,
    total
  };
}
